import { showNavigationDrawer } from '@/actions';
import { Button, Fab } from '@/components/elements/forms/buttons';
import { ListItem, ListItemContent } from '@/components/elements/lists';
import Divider from '@/components/elements/lists/Divider/Divider';
import { InitialsAvatar } from '@/components/elements/redesign/Avatar';
import Icon from '@/components/icons/Icon';
import LogoutModal from '@/components/modules/modals/LogoutModal';
import { EVENT_NAME } from '@/constants/analytics';
import { FINNISH_TRANSLATION_FEATURE_FLAG } from '@/constants/experimentConstants';
import { NAVIGATION_TRIGGER } from '@/constants/navigation';
import { DrawerItem, myAccountItem, navigationDrawerItems } from '@/constants/navigationDrawerConstants';
import { Z_INDEX } from '@/constants/styleConstants';
import { classnames, getUserInitials, isSistaminuten, trackMpEvent, url } from '@/helpers';
import { themed } from '@/helpers/theme';
import { useAppSelector } from '@/hooks';
import { useGetAmplitudeExperimentVariant } from '@/hooks/useAmplitudeExperiment';
import useMobileView from '@/hooks/useMobileView';
import { _s, getLang, setLang } from '@/locale';
import { getScreenName, handleLogoutClick, handleNavItemClicked } from '@/services/navigationServices';
import { NavItemIdentifier } from '@/types/navigation';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { ShadowModal } from '../../modals';
import styles from './NavigationDrawer.module.scss';

const LanguageButton = ({ lang, active, onClick }) => (
  <button
    onClick={onClick}
    className={`!my-1 flex w-full items-center justify-between rounded px-3 py-3 ${
      active ? themed('bg-primary-50', 'bg-sm_primary-100') : 'hover:bg-black-100'
    }`}>
    <div className="text-left">
      <div>{_s(`${lang}Short`)}</div>
      <div className="text-black-600 text-sm">{_s(`${lang}Country`)}</div>
    </div>
    {getLanguageFlagIcon(lang)}
  </button>
);

const getLanguageFlagIcon = (lang) => {
  switch (lang) {
    case 'sv':
      return <Icon variant="swedish" noFilter />;
    case 'en':
      return <Icon variant="english" noFilter />;
    case 'fi':
      return <Icon variant="finnish" noFilter />;
    default:
      return null;
  }
};

const NavigationDrawer = ({ source = 'some_menu' }: { source?: string }) => {
  const user = useAppSelector((state) => state.users?.user);
  const flags = useAppSelector((state) => state.flags);
  const { show: open = false } = useAppSelector((state) => state?.navigationDrawer) as { show: boolean };
  const [show, setShow] = useState(false);
  const [showLanguageModal, setShowLanguageModal] = useState(false);
  const { isMobileView } = useMobileView();
  const dispatch = useDispatch();
  const selectLanguage = (lang) => () => setLang(lang) && setShowLanguageModal(false);
  const currentLang = getLang();
  const isWhitelabel = useAppSelector((state) => state?.settings.isWhitelabel);
  const getVariant = useGetAmplitudeExperimentVariant();
  const finnishTranslationFeature =
    flags[FINNISH_TRANSLATION_FEATURE_FLAG]?.value === 'on' ||
    getVariant(FINNISH_TRANSLATION_FEATURE_FLAG)?.value === 'on';

  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const handleLogout = () => handleLogoutClick(dispatch, NAVIGATION_TRIGGER.NAV_DRAWER);

  const handleNavItemClick = (identifier: NavItemIdentifier) => {
    handleNavItemClicked(dispatch, getScreenName(source), NAVIGATION_TRIGGER.NAV_DRAWER)(identifier)();
    dispatch(showNavigationDrawer({ show: false }));
  };
  const sorted = navigationDrawerItems(user, flags, isWhitelabel).reduce(
    (
      acc: {
        account: DrawerItem[];
        payment: DrawerItem[];
        offers: DrawerItem[];
        more: DrawerItem[];
      },
      item,
    ) => {
      switch (item.category) {
        case 'account':
          if (item.identifier === NavItemIdentifier.Bundles && isSistaminuten()) {
            break;
          }
          acc.account.push(item);
          break;
        case 'payment':
          acc.payment.push(item);
          break;
        case 'offer':
          acc.offers.push(item);
          break;
        default:
          acc.more.push(item);
      }

      return acc;
    },
    {
      account: [],
      payment: [],
      offers: [],
      more: [],
    },
  );

  useEffect(() => {
    if (open) setShow(true);
    if (!open && show) {
      setTimeout(() => !open && setShow(false), 200);
    }
  }, [open, show]);

  const handleCancelLogout = () => {
    trackMpEvent(EVENT_NAME.LOGOUT_CANCELED, {
      screen_name: url.getScreenNameFromUrl(window?.location?.href),
      initiated_from: NAVIGATION_TRIGGER.NAV_DRAWER,
    });
    setShowLogoutModal(false);
  };

  return (
    <>
      <LogoutModal
        isOpen={showLogoutModal}
        onClick={handleLogout}
        onClose={handleCancelLogout}
        triggerSource={NAVIGATION_TRIGGER.NAV_DRAWER}
      />
      <div
        id="navigation-drawer"
        className={classnames(
          'py-lg no-scrollbar fixed right-0 top-0 h-full w-full overflow-y-auto bg-white shadow transition-transform duration-200 md:w-[390px] md:px-2',
          open ? 'translate-x-0 md:[transform:translateX(100%)_translateX(-390px)]' : 'translate-x-full',
          show ? '' : 'invisible',
        )}
        style={{ zIndex: Z_INDEX.NAVIGATION_DRAWER }}
        aria-hidden={!open}>
        {!isMobileView && (
          <div className="p-lg">
            <Fab
              id="close-navigation-drawer"
              onClick={() => dispatch(showNavigationDrawer({ show: false }))}
              size="xs"
              variant="primary"
              icon={<Icon variant="close" />}
            />
          </div>
        )}
        <nav>
          <ul className="gap-xs flex flex-col">
            {user && (
              <li>
                <Link
                  to={myAccountItem.to}
                  onClick={() => handleNavItemClick(myAccountItem.identifier)}
                  className="gap-md mx-lg p-lg border-black-200 shadow-xs flex flex-row items-center rounded-md border">
                  <div className="flex-shrink-0">
                    <InitialsAvatar initials={getUserInitials(user)} size="md" variant="default" />
                  </div>
                  <div className="flex flex-1 flex-col gap-1 overflow-hidden">
                    <div className="text-black-900 overflow-hidden text-ellipsis text-xl font-bold">
                      {user.about?.givenName} {user.about?.familyName}
                    </div>
                    <div className="text-m text-black-600 overflow-hidden text-ellipsis">{user.contact?.email}</div>
                    <div className="text-sm">
                      <Button
                        variant="link"
                        className="pb-0 pl-0 pt-2"
                        title={_s('logOut')}
                        onClick={(event) => {
                          event.stopPropagation();
                          event.preventDefault();
                          setShowLogoutModal(true);
                        }}>
                        {_s('logOut')}
                      </Button>
                    </div>
                  </div>
                  <div className="flex-shrink-0">
                    <Icon variant="chevron-right" />
                  </div>
                </Link>
              </li>
            )}

            {!user && <Divider label={_s('manageBooking')} size="sm" />}

            {sorted.account.map((item, key) => {
              return (
                <ListItem
                  key={key}
                  leftSlot={<Icon variant={item.icon} />}
                  rightSlot={<Icon variant={item.externalLink ? 'external-link' : 'chevron-right'} />}
                  targetBlank={item.externalLink}
                  to={item.to}
                  onClick={() => handleNavItemClick(item.identifier)}
                  data-cy={`nav-drawer-${item.identifier}`}>
                  <ListItemContent title={item.label} />
                </ListItem>
              );
            })}

            <Divider label={user ? _s('myPaymentMethods') : _s('paymentMethods')} size="sm" />
            {sorted.payment.map((item, key) => {
              const missingActiveCard = item.identifier === NavItemIdentifier.PaymentMethods && !user?.activeCoF;
              return (
                <ListItem
                  key={key}
                  leftSlot={<Icon variant={item.icon} />}
                  rightSlot={<Icon variant={item.externalLink ? 'external-link' : 'chevron-right'} />}
                  targetBlank={item.externalLink}
                  to={item.to}
                  onClick={() => handleNavItemClick(item.identifier)}
                  data-cy={`nav-drawer-${item.identifier}`}>
                  <ListItemContent
                    title={item.label}
                    badge={missingActiveCard ? <Icon variant="warning" color="danger-700" /> : undefined}
                    {...(missingActiveCard
                      ? { subTitle: _s('missingActiveCard'), subTitleClassName: 'text-xs text-danger-700' }
                      : {})}
                  />
                </ListItem>
              );
            })}

            {!isSistaminuten() && sorted.offers.length > 0 && (
              <>
                <Divider label={_s('offers')} size="sm" />
                {sorted.offers.map((item, key) => {
                  /**
                   * We can remove this when 2023 is over
                   */
                  const isWellnessBuy = item.identifier === NavItemIdentifier.WellnessCardBuy;
                  const daysUntilCampaingEnds = moment('01/01/2024', 'DD/MM/YYYY').diff(moment(), 'days') + 1;
                  const label =
                    isWellnessBuy && daysUntilCampaingEnds > 0 && _s('daysLeft', { count: daysUntilCampaingEnds });

                  return (
                    <ListItem
                      key={key}
                      leftSlot={<Icon variant={item.icon} />}
                      rightSlot={<Icon variant={item.externalLink ? 'external-link' : 'chevron-right'} />}
                      targetBlank={item.externalLink}
                      to={item.to}
                      onClick={() => handleNavItemClick(item.identifier)}>
                      <ListItemContent
                        title={item.label}
                        {...(label && { label: { label, className: 'bg-highlight-500' } })}
                      />
                    </ListItem>
                  );
                })}
              </>
            )}
            <Divider label={_s('More')} size="sm" />
            {sorted.more.map((item, key) => (
              <ListItem
                key={key}
                leftSlot={<Icon variant={item.icon} />}
                rightSlot={<Icon variant={item.externalLink ? 'external-link' : 'chevron-right'} />}
                targetBlank={item.externalLink}
                to={item.to}
                onClick={() => handleNavItemClick(item.identifier)}>
                <ListItemContent title={item.label} />
              </ListItem>
            ))}
            <ListItem
              className="relative"
              leftSlot={getLanguageFlagIcon(currentLang)}
              rightSlot={<Icon variant="chevron-right" />}
              onClick={() => setShowLanguageModal(true)}>
              <ListItemContent title={_s(currentLang)} />
            </ListItem>
          </ul>
        </nav>
        {isMobileView && <div style={{ height: 80 }}></div>}
      </div>
      {show && (
        <div
          className={classnames(
            'fixed left-0 top-0 h-full w-full cursor-pointer bg-[rgba(0,0,0,0.2)] bg-opacity-20',
            open && styles.show,
            !open && styles.hide,
          )}
          style={{ zIndex: Z_INDEX.NAVIGATION_DRAWER_OVERLAY }}
          onClick={() => dispatch(showNavigationDrawer({ show: false }))}></div>
      )}
      <ShadowModal isOpen={showLanguageModal} onRequestClose={() => setShowLanguageModal(false)} className="relative">
        <button
          className={`absolute right-4 top-4 focus:outline-none focus:ring ${themed(
            'focus:ring-primary-200',
            'focus:ring-sm_primary',
          )} rounded p-1`}
          onClick={() => setShowLanguageModal(false)}>
          <Icon variant="close" className="text-black-900 h-10 w-10" />
        </button>
        <h2 className="py-3 text-3xl">{_s('offCanvasNavigation.selectLanguageModal.title')}</h2>
        <LanguageButton lang="sv" onClick={selectLanguage('sv')} active={currentLang === 'sv'} />
        <LanguageButton lang="en" onClick={selectLanguage('en')} active={currentLang === 'en'} />
        {finnishTranslationFeature && (
          <LanguageButton lang="fi" onClick={selectLanguage('fi')} active={currentLang === 'fi'} />
        )}
        <Button variant="tertiary" block size="sm" className="mt-4" onClick={() => setShowLanguageModal(false)}>
          {_s('close')}
        </Button>
      </ShadowModal>
    </>
  );
};

export default NavigationDrawer;
